import { ArrowRight, CheckCircle, UserCheck, Zap } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import type { DashboardStats } from '@/services/dashboardApi'
import { DashboardCard } from './DashboardCard'

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  const intervals = [
    { label: 'año', seconds: 31_536_000 },
    { label: 'mes', seconds: 2_592_000 },
    { label: 'día', seconds: 86_400 },
    { label: 'hora', seconds: 3_600 },
    { label: 'minuto', seconds: 60 },
    { label: 'segundo', seconds: 1 },
  ]
  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds)
    if (count >= 1) {
      const plural = count !== 1 ? (interval.label === 'mes' ? 'es' : 's') : ''
      return `${count} ${interval.label}${plural}`
    }
  }
  return 'Justo ahora'
}

export interface ActivityTimelineProps {
  activity: DashboardStats['recentActivity']
  hasChannels: boolean
  /** Estado inicial de la sección colapsable (por defecto contraído: `false`). */
  defaultExpanded?: boolean
}

/** Actividad reciente en formato timeline; cada ítem abre su conversación. */
export function ActivityTimeline({
  activity,
  hasChannels,
  defaultExpanded = false,
}: ActivityTimelineProps) {
  const navigate = useNavigate()
  const { profile } = useVerticalUI()
  const customerRole = profile.terminology.customerRoleLabel.toLowerCase()

  return (
    <DashboardCard
      title="Actividad del Sistema"
      titleId="activity-title"
      collapsible
      defaultExpanded={defaultExpanded}
      state={activity.length === 0 ? 'empty' : 'success'}
      emptyMessage={`Sin actividad reciente. Los mensajes de tus ${customerRole}s aparecerán aquí.`}
      emptyAction={
        hasChannels ? (
          <p className="flex items-center gap-1 text-xs font-medium text-brand">
            <Zap className="h-4 w-4" aria-hidden="true" />
            Tus canales están conectados y listos
          </p>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/app/instances')}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
          >
            Ver canales
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </button>
        )
      }
      action={
        activity.length > 0 ? (
          <Link
            to="/app/conversations"
            className="flex items-center gap-1 text-xs font-medium text-brand hover:text-brand-dark"
          >
            Ver todas
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        ) : null
      }
    >
      <ol className="relative list-none space-y-5 p-4 before:absolute before:bottom-2 before:left-[33px] before:top-2 before:w-[2px] before:bg-slate-100">
        {activity.slice(0, 5).map((item) => {
          const isInbound = item.direction === 'inbound'
          return (
            <li key={item.id} className="relative pl-12">
              <div
                className={`absolute left-0 top-0.5 z-10 flex h-9 w-9 items-center justify-center rounded-full ring-4 ring-white ${
                  isInbound ? 'bg-brand/10 text-brand' : 'bg-indigo-500/10 text-indigo-600'
                }`}
              >
                {isInbound ? (
                  <UserCheck className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <CheckCircle className="h-4 w-4" aria-hidden="true" />
                )}
              </div>
              <Link
                to={`/app/conversations/${item.id}`}
                className="block rounded-xl border border-slate-100 bg-slate-50/50 p-3 transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <div className="mb-1 flex items-start justify-between gap-2">
                  <p className="text-[13px] font-bold text-slate-900">
                    {isInbound ? 'Nuevo mensaje' : 'Respuesta enviada'}
                  </p>
                  <span className="rounded bg-white px-1.5 py-0.5 text-xs font-medium text-slate-400 shadow-sm">
                    {timeAgo(item.timestamp)}
                  </span>
                </div>
                <p className="truncate text-[12px] text-slate-600">
                  {isInbound ? `De ${item.contact}` : `A ${item.contact}`} — {item.message}
                </p>
              </Link>
            </li>
          )
        })}
      </ol>
    </DashboardCard>
  )
}
